import { useCallback, useEffect, useRef, useState } from 'react';
import { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import { DataTableFilterKind } from './DataTable.types';
import {
    buildServerLoadOptions,
    IServerLoadOptions,
    ServerLoader,
    ServerRowPageLocator,
} from './dataTableServerSource';

interface IUseServerDataArgs<T> {
    /** Загрузчик; без него таблица работает на клиентских данных. */
    load?: ServerLoader<T>;
    pageIndex: number;
    pageSize: number;
    sorting: SortingState;
    columnFilters: ColumnFiltersState;
    globalFilter?: string;
    searchFields?: string[];
    /** Вид фильтра по полю (см. buildServerLoadOptions). */
    filterKinds?: Record<string, DataTableFilterKind>;
    /** Внешний сигнал «перечитать» (после сохранения, действия над строкой). */
    refreshToken?: number;
    /** Задержка перед запросом: ввод в фильтре не должен бить по серверу на каждый символ. */
    debounceMs?: number;
    /** Подгрузка по мере прокрутки вместо страниц: строки накапливаются. */
    infinite?: boolean;
    /** Сохранённый абсолютный индекс строки: стартовое окно открывается вокруг
     *  её порции, без загрузки всех предыдущих строк. */
    initialRowIndex?: number;
    /** Стабильный ключ строки из URL. При наличии locator он имеет приоритет
     *  над сохранённым индексом, который остаётся быстрым fallback. */
    initialRowId?: string;
    locateInitialChunk?: ServerRowPageLocator;
    /** Смена значения объявляет текущую выборку другой (смена предустановленного
     *  фильтра папки): окно и накопленные строки начинаются заново. */
    resetToken?: number;
    /** Перечитывание (refreshToken) сохраняет накопленный объём: запрашивается
     *  столько же строк, сколько уже показано, а не первая порция. */
    keepCountOnReload?: boolean;
}

export interface IServerDataState<T> {
    rows: T[];
    totalCount: number;
    loading: boolean;
    error: unknown;
    /** Отказ пришёл на подгрузку следующей порции: показанные строки целы,
     *  не хватает только продолжения списка. */
    errorOnLoadMore?: boolean;
    /** Повторить последнюю выборку — «Повторить» в сообщении об отказе. */
    reload?: () => void;
    /** Последние отправленные параметры — нужны экспорту и серверным «видам». */
    loadOptions?: IServerLoadOptions;
    /** Подгрузка следующей порции (режим прокрутки); за пределом данных — no-op. */
    loadMore?: () => void;
    /** Подгрузка предыдущей порции перед уже показанными строками. */
    loadPrevious?: () => void;
    /** Заменить текущее окно первой серверной порцией. */
    jumpToStart?: () => void;
    /** Есть ли ещё непрогруженные строки (режим прокрутки). */
    hasMore?: boolean;
    /** Есть ли строки перед текущим окном. */
    hasPrevious?: boolean;
    /** Абсолютный индекс первой строки текущего окна в серверной выборке. */
    windowStartIndex?: number;
    /** Какая граница окна сейчас загружается. */
    loadingDirection?: 'initial' | 'previous' | 'next' | 'refresh';
    /** Строки на экране относятся к прежней выборке: ответ на изменившуюся
     *  сортировку или фильтр ещё не пришёл. Якорь по таким строкам не считается. */
    stale?: boolean;
}

/** Внутреннее состояние хука: плюс ключ выборки, которой принадлежат rows. */
interface IServerDataInternalState<T> extends IServerDataState<T> {
    appliedSelectionKey?: string;
}

type InfiniteDirection = 'replace' | 'previous' | 'next';

interface IInfiniteRange {
    key: string;
    firstChunk: number;
    lastChunk: number;
    direction: InfiniteDirection;
    pending: boolean;
}

interface IServerRequest {
    key: string;
    selectionKey: string;
    refreshToken: number;
    resetToken: number;
    retryToken: number;
    infinite: boolean;
    options: IServerLoadOptions;
    direction: InfiniteDirection;
    loadingDirection: IServerDataState<unknown>['loadingDirection'];
    immediate: boolean;
}

/**
 * Данные таблицы с сервера: состояние таблицы переводится в параметры выборки и
 * отправляется загрузчику. Устаревшие ответы отбрасываются (гонка при быстрой
 * смене страницы или фильтра), одинаковые параметры не запрашиваются повторно.
 */
export function useDataTableServerData<T>({
    load,
    pageIndex,
    pageSize,
    sorting,
    columnFilters,
    globalFilter,
    searchFields,
    filterKinds,
    refreshToken = 0,
    debounceMs = 250,
    infinite = false,
    initialRowIndex,
    initialRowId,
    locateInitialChunk,
    resetToken = 0,
    keepCountOnReload = false,
}: IUseServerDataArgs<T>): IServerDataState<T> {
    const [state, setState] = useState<IServerDataInternalState<T>>({
        rows: [],
        totalCount: 0,
        loading: !!load,
        error: undefined,
    });
    const [retryToken, setRetryToken] = useState(0);
    // План запроса переживает отмену выполнения: повтор должен сохранить и окно,
    // и способ применения строк. Дедуплицируем только завершённый план.
    const requestRef = useRef<IServerRequest | undefined>(undefined);
    const settledRequestRef = useRef<{ request: IServerRequest; failed: boolean } | undefined>(undefined);
    // Последний запущенный план: отменённый запуск уже выставил loading, и
    // вернуться к прежней выборке без нового запроса тогда нельзя.
    const startedRequestRef = useRef<IServerRequest | undefined>(undefined);
    const loadRef = useRef(load);
    loadRef.current = load;
    // Виды фильтров меняются только вместе со схемой колонок (и с самими
    // фильтрами) — сами по себе перезапрос не начинают.
    const filterKindsRef = useRef(filterKinds);
    filterKindsRef.current = filterKinds;

    const run = useCallback((request: IServerRequest, controller: AbortController) => {
        const loader = loadRef.current;
        if (!loader) return;
        const { options, selectionKey, direction, loadingDirection } = request;
        startedRequestRef.current = request;
        setState((prev) => ({
            ...prev,
            loading: true,
            loadingDirection,
            error: undefined,
            errorOnLoadMore: false,
        }));
        loader(options, controller.signal)
            .then((result) => {
                // ответ на устаревший запрос игнорируем: иначе на экран вернутся
                // строки предыдущей страницы или снятого фильтра
                if (controller.signal.aborted) return;
                settledRequestRef.current = { request, failed: false };
                setState((prev) => ({
                    rows:
                        direction === 'next'
                            ? [...prev.rows, ...result.rows]
                            : direction === 'previous'
                              ? [...result.rows, ...prev.rows]
                              : result.rows,
                    totalCount: result.totalCount,
                    loading: false,
                    loadingDirection: undefined,
                    error: undefined,
                    loadOptions: options,
                    appliedSelectionKey: selectionKey,
                    windowStartIndex: direction === 'next' ? (prev.windowStartIndex ?? 0) : Math.max(0, options.skip),
                }));
            })
            .catch((error: unknown) => {
                if (controller.signal.aborted) return;
                settledRequestRef.current = { request, failed: true };
                setState((prev) => ({
                    ...prev,
                    loading: false,
                    loadingDirection: undefined,
                    error,
                    errorOnLoadMore: direction !== 'replace',
                }));
            });
    }, []);

    // состояние таблицы приходит новыми объектами на каждый рендер — сравниваем по значению
    const sortingKey = JSON.stringify(sorting);
    const filtersKey = JSON.stringify(columnFilters);
    const searchFieldsKey = JSON.stringify(searchFields);

    /** Что делает выборку другой: её смена начинает накопление порций заново.
     *  Обновление (refreshToken) входит сюда только без keepCountOnReload — с ним
     *  перечитывание идёт одним широким запросом на всё показанное. */
    const selectionKey =
        `${sortingKey}|${filtersKey}|${globalFilter ?? ''}|${searchFieldsKey}|${pageSize}|${resetToken}` +
        (keepCountOnReload ? '' : `|${refreshToken}`) +
        (infinite ? '' : `|${pageIndex}`);

    const initialSelectionKeyRef = useRef(selectionKey);
    const shouldLocateInitialRef = useRef(
        infinite && !!load && !!initialRowId && !!locateInitialChunk,
    );
    const [initialLookupPending, setInitialLookupPending] = useState(shouldLocateInitialRef.current);

    // Стартовая порция — вокруг сохранённой строки; захват соседних порций
    // позволяет найти rowId, даже если его немного сдвинули новые строки.
    const firstChunkRef = useRef(Math.max(0, Math.floor((initialRowIndex ?? 0) / Math.max(1, pageSize))));
    const initialRadiusRef = useRef(initialRowId != null || (initialRowIndex ?? 0) > 0 ? 1 : 0);
    // Окно загруженных порций. activeRange синхронно даёт новой выборке нулевую
    // порцию, а range догоняет его эффектом после commit. Так новый фильтр не
    // уходит со старым skip и при этом в render нет побочного setState.
    const [range, setRange] = useState<IInfiniteRange>(() => ({
        key: selectionKey,
        firstChunk: Math.max(0, firstChunkRef.current - initialRadiusRef.current),
        lastChunk: firstChunkRef.current + initialRadiusRef.current,
        direction: 'replace',
        pending: false,
    }));
    // Постраничная выборка стала лентой (полноэкранный режим таблицы): накопление идёт не с
    // начала, а с порции текущей страницы — пользователь остаётся на тех же строках, а
    // предыдущие порции догружаются прокруткой вверх.
    const wasInfiniteRef = useRef(infinite);
    const startChunk = infinite && !wasInfiniteRef.current ? Math.max(0, pageIndex) : 0;
    const activeRange: IInfiniteRange =
        range.key === selectionKey
            ? range
            : {
                  key: selectionKey,
                  firstChunk: startChunk,
                  lastChunk: startChunk,
                  direction: 'replace',
                  pending: false,
              };

    // Новую выборку сразу считаем окном от начала, но само состояние меняем
    // после commit. setState во время render в StrictMode мог относиться ещё к
    // не смонтированному дереву и оставлять сортировку/фильтр только в UI и URL.
    useEffect(() => {
        setRange((prev) =>
            prev.key === selectionKey
                ? prev
                : {
                      key: selectionKey,
                      firstChunk: startChunk,
                      lastChunk: startChunk,
                      direction: 'replace',
                      pending: false,
                  },
        );
        wasInfiniteRef.current = infinite;
        // startChunk вычислен в том же рендере, что и selectionKey
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectionKey]);

    useEffect(() => {
        if (!shouldLocateInitialRef.current || !initialRowId || !locateInitialChunk) return;
        // Cursor относится только к состоянию, прочитанному при монтировании.
        // Если пользователь уже поменял выборку, она закономерно стартует с нуля.
        if (selectionKey !== initialSelectionKeyRef.current) {
            setInitialLookupPending(false);
            return;
        }

        const controller = new AbortController();
        const options = buildServerLoadOptions({
            pageIndex: 0,
            pageSize,
            sorting,
            columnFilters,
            globalFilter,
            searchFields,
            filterKinds: filterKindsRef.current,
        });
        locateInitialChunk(initialRowId, options, controller.signal)
            .then((chunk) => {
                if (controller.signal.aborted) return;
                if (chunk != null && Number.isFinite(chunk) && chunk >= 0) {
                    const safeChunk = Math.floor(chunk);
                    setRange({
                        key: selectionKey,
                        firstChunk: Math.max(0, safeChunk - initialRadiusRef.current),
                        lastChunk: safeChunk + initialRadiusRef.current,
                        direction: 'replace',
                        pending: false,
                    });
                }
                setInitialLookupPending(false);
            })
            .catch(() => {
                // Старый backend, удалённая строка или временный отказ: сохранённый
                // rowIndex уже задал стартовое окно и позволяет восстановиться без API.
                if (!controller.signal.aborted) setInitialLookupPending(false);
            });

        return () => controller.abort();
        // Cursor и исходная выборка намеренно применяются ровно один раз.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialRowId, locateInitialChunk, selectionKey]);

    useEffect(() => {
        if (!load || (initialLookupPending && selectionKey === initialSelectionKeyRef.current)) return;
        // В режиме прокрутки запрашивается изменившаяся граница окна.
        const targetChunk = activeRange.direction === 'previous' ? activeRange.firstChunk : activeRange.lastChunk;
        let options = buildServerLoadOptions({
            pageIndex: infinite ? targetChunk : pageIndex,
            pageSize,
            sorting,
            columnFilters,
            globalFilter,
            searchFields,
            filterKinds: filterKindsRef.current,
        });
        if (infinite && activeRange.direction === 'replace' && activeRange.firstChunk !== activeRange.lastChunk) {
            options = {
                ...options,
                skip: activeRange.firstChunk * pageSize,
                take: (activeRange.lastChunk - activeRange.firstChunk + 1) * pageSize,
            };
        }
        const key = JSON.stringify({
            selectionKey,
            options,
            infinite,
            range: infinite ? [activeRange.firstChunk, activeRange.lastChunk, activeRange.direction] : undefined,
            refreshToken,
        });
        const previous = requestRef.current;
        const settled = settledRequestRef.current;
        const sameRequestKey = previous?.key === key;
        const retried = previous !== undefined && previous.retryToken !== retryToken;
        let request: IServerRequest;
        if (sameRequestKey && !retried) {
            request = previous;
        } else if (sameRequestKey && settled?.request === previous && settled.failed) {
            // «Повторить» после ошибки воспроизводит запрос, включая широкое
            // обновление окна или догрузку его границы.
            request = { ...previous, retryToken, immediate: true };
        } else if (
            !retried &&
            settled &&
            !settled.failed &&
            settled.request.key === key &&
            startedRequestRef.current === settled.request
        ) {
            // Выборка вернулась к показанной до истечения debounce (A → B → A):
            // строки на экране уже её, повторный запрос не нужен.
            request = settled.request;
        } else {
            const refreshed = previous !== undefined && previous.refreshToken !== refreshToken;
            const modeChanged = previous !== undefined && previous.infinite !== infinite;
            // сброс объявляет выборку другой (новый загрузчик, другой набор записей) — без задержки ввода
            const reset = previous !== undefined && previous.resetToken !== resetToken;
            const reloadWindow = infinite && ((keepCountOnReload && refreshed) || retried);
            const direction = infinite && !refreshed && !retried && !modeChanged ? activeRange.direction : 'replace';
            if (reloadWindow) {
                options = {
                    ...options,
                    skip: activeRange.firstChunk * pageSize,
                    take: (activeRange.lastChunk - activeRange.firstChunk + 1) * pageSize,
                };
                setRange((prev) => (prev.pending ? { ...prev, pending: false } : prev));
            }
            request = {
                key,
                selectionKey,
                refreshToken,
                resetToken,
                retryToken,
                infinite,
                options,
                direction: reloadWindow ? 'replace' : direction,
                loadingDirection: reloadWindow
                    ? 'refresh'
                    : direction === 'previous'
                      ? 'previous'
                      : direction === 'next'
                        ? 'next'
                        : 'initial',
                immediate: !previous || reloadWindow || direction !== 'replace' || retried || modeChanged || reset,
            };
        }
        requestRef.current = request;
        if (settledRequestRef.current?.request === request) return;

        const controller = new AbortController();
        const timer = request.immediate ? null : setTimeout(() => run(request, controller), debounceMs);
        if (request.immediate) run(request, controller);
        return () => {
            if (timer) clearTimeout(timer);
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        load,
        pageIndex,
        pageSize,
        sortingKey,
        filtersKey,
        globalFilter,
        searchFieldsKey,
        refreshToken,
        resetToken,
        debounceMs,
        infinite,
        keepCountOnReload,
        activeRange.firstChunk,
        activeRange.lastChunk,
        activeRange.direction,
        retryToken,
        initialLookupPending,
        run,
    ]);

    // Сохранённая позиция могла оказаться за концом укоротившейся выборки.
    // После первого пустого ответа прыгаем к её настоящей последней порции.
    useEffect(() => {
        // Два IntersectionObserver-события до ререндера не должны сдвинуть окно
        // дважды. Снимаем синхронный замок только после ответа на новую границу.
        const targetSkip =
            (activeRange.direction === 'previous' ? activeRange.firstChunk : activeRange.lastChunk) * pageSize;
        const rangeRequestSettled =
            activeRange.pending &&
            !state.loading &&
            (state.error !== undefined || state.loadOptions?.skip === targetSkip);
        if (rangeRequestSettled) {
            setRange((prev) => (prev.pending ? { ...prev, pending: false } : prev));
        }
        if (!infinite || state.loading || state.rows.length > 0 || state.totalCount <= 0 || activeRange.firstChunk === 0)
            return;
        const lastChunk = Math.floor((state.totalCount - 1) / Math.max(1, pageSize));
        setRange({ key: selectionKey, firstChunk: lastChunk, lastChunk, direction: 'replace', pending: false });
    }, [
        infinite,
        state.loading,
        state.rows.length,
        state.totalCount,
        state.error,
        state.loadOptions?.skip,
        activeRange.firstChunk,
        activeRange.lastChunk,
        activeRange.direction,
        activeRange.pending,
        pageSize,
        selectionKey,
    ]);

    const windowStartIndex = state.windowStartIndex ?? activeRange.firstChunk * pageSize;
    const hasPrevious = infinite && windowStartIndex > 0;
    // Граница серверной выборки определяется абсолютным номером следующего чанка,
    // а не числом реально добавленных строк. Потребитель может отфильтровать часть
    // ответа или сервер может вернуть totalCount больше доступного хвоста. В таком
    // случае rows.length перестаёт расти, сентинел остаётся видимым и прежняя
    // проверка бесконечно запрашивала skip за пределами totalCount.
    const nextChunkStart = (activeRange.lastChunk + 1) * Math.max(1, pageSize);
    // take=0 запрашивает весь набор: следующей порции у такого списка нет.
    const hasMore = infinite && pageSize > 0 && nextChunkStart < state.totalCount;
    const loadMore = useCallback(() => {
        if (!loadRef.current) return;
        setRange((prev) => {
            const current =
                prev.key === selectionKey
                    ? prev
                    : {
                          key: selectionKey,
                          firstChunk: 0,
                          lastChunk: 0,
                          direction: 'replace' as const,
                          pending: false,
                      };
            if (current.pending) return prev;
            return { ...current, lastChunk: current.lastChunk + 1, direction: 'next', pending: true };
        });
    }, [selectionKey]);
    const loadPrevious = useCallback(() => {
        if (!loadRef.current) return;
        setRange((prev) => {
            const current =
                prev.key === selectionKey
                    ? prev
                    : {
                          key: selectionKey,
                          firstChunk: 0,
                          lastChunk: 0,
                          direction: 'replace' as const,
                          pending: false,
                      };
            if (current.pending || current.firstChunk === 0) return prev;
            return {
                ...current,
                firstChunk: Math.max(0, current.firstChunk - 1),
                direction: 'previous',
                pending: true,
            };
        });
    }, [selectionKey]);
    const jumpToStart = useCallback(() => {
        // Cursor нужен только при первом восстановлении. Ручной переход — новое
        // намерение пользователя и всегда адресует первую порцию текущей выборки.
        shouldLocateInitialRef.current = false;
        setInitialLookupPending(false);
        setRange({ key: selectionKey, firstChunk: 0, lastChunk: 0, direction: 'replace', pending: false });
    }, [selectionKey]);
    const reload = useCallback(() => setRetryToken((token) => token + 1), []);

    const settledSelection = state.appliedSelectionKey === selectionKey;
    if (!infinite) return { ...state, stale: !!load && !settledSelection, reload };
    // после отказа прокрутка не долбит сервер сама: продолжение просит «Повторить»
    // Пока на экране строки прежней выборки, её границы тоже прежние. Не даём
    // сентинелам запросить next/previous и перебить ожидающий debounce новой
    // сортировки или фильтра.
    const canLoadMore = settledSelection && hasMore && !state.loading && !state.error;
    const canLoadPrevious = settledSelection && hasPrevious && !state.loading && !state.error;
    return {
        ...state,
        stale: state.appliedSelectionKey !== selectionKey,
        windowStartIndex,
        hasMore,
        hasPrevious,
        reload,
        loadMore: canLoadMore ? loadMore : undefined,
        loadPrevious: canLoadPrevious ? loadPrevious : undefined,
        jumpToStart,
    };
}
